// Helper functions for hotel operations
import { searchRooms } from './searchHelpers';

/**
 * Get rooms for a specific hotel (from state)
 * @param {string} hotelId - The hotel ID
 * @param {Object} rooms - The rooms state object
 * @returns {Array} - The array of rooms for the hotel
 */
export const getRoomsForHotel = (hotelId, rooms) => {
  return rooms[hotelId] || [];
};

/**
 * Function to initiate hotel deletion with confirmation
 * @param {Object} hotel - The hotel object to delete
 * @param {Function} setHotelToDelete - State setter for hotel to delete
 * @param {Function} setShowDeleteConfirm - State setter for showing delete confirm
 */
export const confirmDeleteHotel = (hotel, setHotelToDelete, setShowDeleteConfirm) => {
  setHotelToDelete(hotel);
  setShowDeleteConfirm(true);
};

/**
 * Room form validation
 * @param {Object} roomFormData - Current room form data
 * @param {Function} validateRoomForm - Function to validate room form
 * @returns {Object} - Validation result
 */
export const validateRoomFormHelper = (roomFormData, validateRoomForm) => {
  return validateRoomForm(roomFormData);
};

/**
 * Search rooms function for a specific hotel
 * @param {Object} hotel - The hotel object
 * @param {Function} setRoomSearchLoading - State setter for room search loading
 * @param {Function} setRoomSearchError - State setter for room search error
 * @param {Function} setSelectedHotelForRoomSearch - State setter for selected hotel for room search
 * @param {Function} setRoomSearchResults - State setter for room search results
 * @param {Function} setShowRoomSearchResults - State setter for showing room search results
 * @param {string} TRAVEL_API_BASE - The API base URL
 * @param {Object} travelomatixRoomsAPI - The travelomatix rooms API object
 */
export const searchRoomsHelper = async (hotel, setRoomSearchLoading, setRoomSearchError, setSelectedHotelForRoomSearch, setRoomSearchResults, setShowRoomSearchResults, TRAVEL_API_BASE, travelomatixRoomsAPI) => {
  await searchRooms(hotel, setRoomSearchLoading, setRoomSearchError, setSelectedHotelForRoomSearch, setRoomSearchResults, setShowRoomSearchResults, TRAVEL_API_BASE, travelomatixRoomsAPI);
};

/**
 * Format hotel data for submission to the API
 * @param {Object} formData - The form data from the hotel form
 * @returns {Object} - Formatted data ready for API submission
 */
export const formatHotelDataForSubmission = (formData) => {
  // Filter out empty image URLs
  const validImageUrls = formData.images_urls.filter(url => url.trim() !== '');
  
  const submitData = {
    name_en: formData.name_en,
    featured: formData.featured,
    city: formData.city,
    country: formData.country,
    images_urls: validImageUrls.length > 0 ? validImageUrls : undefined,
    supplier_id: formData.supplier_id,
    commission_percentage: parseFloat(formData.commission_percentage),
    // Add accessibility template IDs
    accessibility_template_ids: formData.accessibility_template_ids || [],
    // Add amenities IDs
    amenities: formData.amenities || []
  };
  
  // Remove undefined fields
  Object.keys(submitData).forEach(key => {
    if (submitData[key] === undefined) {
      delete submitData[key];
    }
  });
  
  return submitData;
};

/**
 * Validate hotel form data
 * @param {Object} formData - The form data to validate
 * @param {boolean} isEditing - Whether we're editing an existing hotel
 * @returns {Object} - Validation errors (empty if valid)
 */
export const validateHotelForm = (formData, isEditing) => {
  const errors = {};
  
  if (!formData.name_en.trim()) {
    errors.name_en = 'English name is required';
  }
  
  if (!formData.city.trim()) {
    errors.city = 'City is required';
  }
  
  if (!formData.country.trim()) {
    errors.country = 'Country is required';
  }
  
  // Image validation - don't require any images for either adding or editing
  const nonEmptyUrls = formData.images_urls.filter(url => url.trim() !== '');
  if (nonEmptyUrls.some(url => url && !/^https?:\/\/.+\..+/.test(url))) {
    errors.images_urls = 'All image URLs must be valid URLs';
  }
  
  const commissionValue = parseFloat(formData.commission_percentage);
  if (isNaN(commissionValue) || commissionValue < 0 || commissionValue > 100) {
    errors.commission_percentage = 'Commission percentage must be between 0 and 100';
  }
  
  return errors;
};

/**
 * Initialize hotel form data for editing
 * @param {Object} hotel - The existing hotel object
 * @returns {Object} - Initialized form data
 */
export const initializeHotelFormDataForEdit = (hotel) => {
  // Extract accessibility template IDs from the nested accessibility_template structure
  let accessibilityTemplateIds = [];
  
  if (hotel.accessibility_template && typeof hotel.accessibility_template === 'object') {
    // Iterate through each category in accessibility_template
    Object.values(hotel.accessibility_template).forEach(category => {
      if (category && typeof category === 'object') {
        // Process feature_categories
        if (Array.isArray(category.feature_categories)) {
          category.feature_categories.forEach(featureCategory => {
            if (featureCategory && featureCategory.id) {
              accessibilityTemplateIds.push(featureCategory.id);
            }
            // Process features within feature categories
            if (Array.isArray(featureCategory.features)) {
              featureCategory.features.forEach(feature => {
                if (feature && feature.id) {
                  accessibilityTemplateIds.push(feature.id);
                }
              });
            }
          });
        }
        
        // Process features directly under category
        if (Array.isArray(category.features)) {
          category.features.forEach(feature => {
            if (feature && feature.id) {
              accessibilityTemplateIds.push(feature.id);
            }
          });
        }
      }
    });
  }
  
  return {
    name_en: hotel.name_en || '',
    featured: hotel.featured || false,
    city: hotel.city || '',
    country: hotel.country || '',
    images_urls: hotel.images_urls && Array.isArray(hotel.images_urls) ? hotel.images_urls : [''],
    supplier_id: hotel.supplier_id || '',
    commission_percentage: hotel.commission_percentage || 10,
    // Add extracted accessibility template IDs
    accessibility_template_ids: accessibilityTemplateIds,
    // Add amenities IDs - handle different possible structures
    amenities: Array.isArray(hotel.amenities) 
      ? hotel.amenities.map(amenity => {
          // If amenities is an array of objects, extract the IDs
          if (typeof amenity === 'object' && amenity.id) {
            return amenity.id;
          }
          // If amenities is already an array of IDs
          return amenity;
        })
      : (hotel.amenities_ids && Array.isArray(hotel.amenities_ids) 
        ? hotel.amenities_ids 
        : [])
  };
};

export default {
  getRoomsForHotel,
  confirmDeleteHotel,
  validateRoomFormHelper,
  searchRoomsHelper,
  formatHotelDataForSubmission,
  validateHotelForm,
  initializeHotelFormDataForEdit
};